import type { Tutorial } from '../../shared/types.js';

export const connectGithubTutorial: Tutorial = {
  name: 'connect-github',
  title: 'Connect GitHub for repo webhooks',
  description:
    'Install the Reflex GitHub App on your organization so repository events are delivered via webhooks and agents can work against your repos.',
  category: 'integrations',
  docPages: ['concepts/plugins', 'administration/organization-onboarding'],
  agenda: [
    'Find the GitHub App card',
    'Install it on an organization',
    'Finish the connection on GitHub',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Connect GitHub',
      body: 'Reflex connects to GitHub through a GitHub App. Installing the App on your GitHub organization grants repo access and wires up the webhooks that drive repo events. This page is where you start that install.',
      path: '/configure/connect/plugins/github',
    },
    {
      id: 'app-card',
      kind: 'info',
      title: 'GitHub App installations',
      body: 'This card shows the GitHub App for your deployment and which accounts it is installed on. If you do not see an install action, ask a platform admin to finish GitHub service setup first.',
      target: '[data-testid="github-user-app-card"]',
      placement: 'top',
    },
    {
      id: 'install',
      kind: 'action',
      trigger: 'click',
      title: 'Install on a GitHub organization',
      body: 'Click the highlighted install action ("Install on a GitHub Organization", or "Add another installation" if you already have one). This opens GitHub in a new tab where you pick the org and the repositories to grant access to.',
      target: '[data-tutorial-id="github-install-org"]',
      placement: 'top',
    },
    {
      id: 'github-handoff',
      kind: 'info',
      title: 'Finish on GitHub',
      body: 'On github.com, choose the organization, then select "All repositories" or specific repos and confirm. That repo selection is what enables the webhooks; the App\'s webhook URL and secret were configured during setup. GitHub then redirects you back here.',
    },
    {
      id: 'done',
      kind: 'info',
      title: 'Connected',
      body: 'Once the redirect completes, the installation appears under "Installed on". Use "Configure on GitHub" later to change which repositories Reflex can access.',
    },
  ],
  // Now that the App's webhooks are live, the natural next step is a flow that
  // reacts to them. Hand off into the GitHub-event flow tour, and offer the
  // scheduled (cron) review as a secondary "see also".
  handoff: {
    tutorialName: 'github-pr-flow',
    label: 'Build an automation that responds to GitHub PRs',
  },
  related: [
    { tutorialName: 'security-review-flow', label: 'Schedule a recurring security review' },
  ],
};
